<template>
  <div class="notes-layer-panel">
    <div class="notes-layer-panel__header">
      <span class="notes-layer-panel__title">Map Notes</span>
      <span class="notes-layer-panel__count">{{ loadedCount }} loaded</span>
    </div>

    <div class="notes-layer-panel__actions">
      <Button
        :label="isVisible ? 'Hide Notes' : 'Show Notes'"
        icon="pi pi-comments"
        size="small"
        severity="secondary"
        :outlined="!isVisible"
        @click="toggleLayerVisibility"
      />
      <Button
        label="Refresh"
        icon="pi pi-refresh"
        size="small"
        severity="secondary"
        outlined
        :disabled="!isVisible || isLoading"
        @click="refreshLayer"
      />
      <Button
        :label="isAddMode ? 'Cancel Add' : 'Add Note'"
        :icon="isAddMode ? 'pi pi-times' : 'pi pi-plus'"
        size="small"
        :severity="isAddMode ? 'contrast' : 'secondary'"
        :outlined="!isAddMode"
        :disabled="!isVisible"
        @click="toggleAddMode"
      />
    </div>

    <div v-if="isAddMode" class="notes-layer-panel__section notes-layer-panel__draft">
      <div class="notes-layer-panel__section-title">Create Note</div>
      <p class="notes-layer-panel__hint">Click the map or type coordinates below.</p>
      <div class="notes-layer-panel__coords-row">
        <label class="notes-layer-panel__coord-label">Lat</label>
        <InputText v-model="draftLat" type="number" step="0.000001" size="small" placeholder="e.g. 51.5074" class="notes-layer-panel__coord-input" />
        <label class="notes-layer-panel__coord-label">Lon</label>
        <InputText v-model="draftLon" type="number" step="0.000001" size="small" placeholder="e.g. -1.2789" class="notes-layer-panel__coord-input" />
      </div>
      <Textarea
        v-model="draftNoteText"
        rows="3"
        placeholder="Write note..."
        class="notes-layer-panel__textarea"
      />
      <MultiSelect
        v-model="draftSharedGroupIds"
        :options="shareableGroups"
        optionLabel="name"
        optionValue="id"
        placeholder="Share with groups (optional)"
        class="notes-layer-panel__multiselect"
      />
      <div class="notes-layer-panel__row-actions">
        <Button
          label="Save Note"
          icon="pi pi-check"
          size="small"
          :disabled="!canSaveDraft || isSubmitting"
          @click="createNote"
        />
      </div>
    </div>

    <div v-if="selectedNote" class="notes-layer-panel__section">
      <div class="notes-layer-panel__section-title">Selected Note</div>
      <div class="notes-layer-panel__meta">
        <span>Author: {{ selectedNote.author_username }}</span>
        <span>{{ formatTimestamp(selectedNote.created) }}</span>
      </div>
      <Textarea
        v-model="selectedNoteText"
        rows="3"
        class="notes-layer-panel__textarea"
      />
      <MultiSelect
        v-if="allowSharingEdit"
        v-model="selectedSharedGroupIds"
        :options="shareableGroups"
        optionLabel="name"
        optionValue="id"
        placeholder="Share with groups"
        class="notes-layer-panel__multiselect"
      />
      <div class="notes-layer-panel__row-actions">
        <Button
          label="Update"
          icon="pi pi-save"
          size="small"
          severity="secondary"
          :disabled="isSubmitting"
          @click="updateSelectedNote"
        />
        <Button
          label="Delete"
          icon="pi pi-trash"
          size="small"
          severity="danger"
          outlined
          :disabled="isSubmitting"
          @click="deleteSelectedNote"
        />
      </div>
    </div>

    <p v-if="errorMessage" class="notes-layer-panel__error">{{ errorMessage }}</p>

    <div ref="hoverTooltipElement" class="notes-layer-tooltip">
      <div class="notes-layer-tooltip__meta">
        <span>{{ hoverAuthor }}</span>
        <span>{{ hoverCreated }}</span>
      </div>
      <div class="notes-layer-tooltip__text">{{ hoverNoteText }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useMapStore } from '@/stores/mapStore'
import type Map from 'ol/Map'
import Overlay from 'ol/Overlay'
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import { Vector as VectorSource } from 'ol/source'
import { Vector as VectorLayer } from 'ol/layer'
import { Fill, RegularShape, Stroke, Style, Text as TextStyle } from 'ol/style'
import { fromLonLat, toLonLat } from 'ol/proj'
import type { MapBrowserEvent } from 'ol'

interface GroupItem {
  id: number
  name: string
}

interface NoteItem {
  id: number
  site: number
  author: number
  author_username: string
  note: string
  lat: number
  lon: number
  shared_groups?: GroupItem[]
  created: string
  updated: string
}

interface NoteFeatureProps {
  noteId: number
  noteText: string
  authorUsername: string
  created: string
}

export default defineComponent({
  name: 'MapNotesLayer',
  props: {
    siteId: {
      type: [String, Number],
      required: true
    },
    initialVisible: {
      type: Boolean,
      default: false
    },
    allowSharingEdit: {
      type: Boolean,
      default: false
    }
  },
  emits: ['note-selected', 'visibility-changed', 'notes-loaded'],
  setup(props, { emit }) {
    const authStore = useAuthStore()
    const mapStore = useMapStore()

    const isVisible = ref(props.initialVisible)
    const isLoading = ref(false)
    const isSubmitting = ref(false)
    const isAddMode = ref(false)
    const loadedCount = ref(0)
    const errorMessage = ref('')

    const notes = ref<NoteItem[]>([])
    const shareableGroups = ref<GroupItem[]>([])
    const selectedNote = ref<NoteItem | null>(null)
    const selectedNoteText = ref('')
    const selectedSharedGroupIds = ref<number[]>([])

    const draftLocation = ref<{ lat: number; lon: number } | null>(null)
    const draftLat = ref<string>('')
    const draftLon = ref<string>('')
    const draftNoteText = ref('')
    const draftSharedGroupIds = ref<number[]>([])

    const resolvedSiteId = ref<number | null>(null)
    const hoverTooltipElement = ref<HTMLElement | null>(null)
    const hoverOverlay = ref<Overlay | null>(null)
    const hoverAuthor = ref('')
    const hoverCreated = ref('')
    const hoverNoteText = ref('')

    const notesSource = ref<VectorSource | null>(null)
    const notesLayer = ref<VectorLayer<VectorSource> | null>(null)

    const canSaveDraft = computed(() => {
      return Boolean(draftLocation.value && draftNoteText.value.trim())
    })

    const getMapInstance = (): Map | null => {
      const maybeMap = mapStore.map as unknown as {
        value?: Map | null
        getLayers?: () => unknown
      } | null

      if (!maybeMap) {
        return null
      }

      if (typeof maybeMap.getLayers === 'function') {
        return maybeMap as unknown as Map
      }

      if (maybeMap.value && typeof maybeMap.value.getLayers === 'function') {
        return maybeMap.value
      }

      return null
    }

    const createLayerIfMissing = (): void => {
      if (notesLayer.value) {
        return
      }

      notesSource.value = new VectorSource()
      notesLayer.value = new VectorLayer({
        source: notesSource.value,
        style: new Style({
          image: new RegularShape({
            points: 4,
            radius: 15,
            angle: Math.PI / 4,
            fill: new Fill({ color: 'rgba(255, 255, 255, 0.72)' }),
            stroke: new Stroke({ color: 'rgba(255, 255, 255, 0.9)', width: 1.25 })
          }),
          text: new TextStyle({
            text: '\uea0b',
            font: '18px primeicons',
            fill: new Fill({ color: 'rgba(15, 23, 42, 0.92)' }),
            offsetY: 1
          })
        })
      })
      notesLayer.value.setZIndex(999)
      notesLayer.value.set('starrsNotesLayer', true)
    }

    const addLayerToMap = (): void => {
      const map = getMapInstance()
      if (!map) {
        return
      }

      createLayerIfMissing()
      if (!notesLayer.value) {
        return
      }

      const alreadyOnMap = map.getLayers().getArray().includes(notesLayer.value)
      if (!alreadyOnMap) {
        map.addLayer(notesLayer.value)
      }
      notesLayer.value.setVisible(isVisible.value)
    }

    const removeLayerFromMap = (): void => {
      const map = getMapInstance()
      if (!map || !notesLayer.value) {
        return
      }

      if (map.getLayers().getArray().includes(notesLayer.value)) {
        map.removeLayer(notesLayer.value)
      }
    }

    const toFeature = (note: NoteItem): Feature<Point> => {
      const feature = new Feature<Point>({
        geometry: new Point(fromLonLat([note.lon, note.lat]))
      })
      const props: NoteFeatureProps = {
        noteId: note.id,
        noteText: note.note,
        authorUsername: note.author_username,
        created: note.created
      }
      feature.setProperties(props)
      return feature
    }

    const formatTimestamp = (value: string): string => {
      if (!value) {
        return ''
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return value
      }
      return date.toLocaleString()
    }

    const truncateNotePreview = (value: string): string => {
      const compact = String(value ?? '').replace(/\s+/g, ' ').trim()
      if (compact.length <= 140) {
        return compact
      }
      return `${compact.slice(0, 137)}...`
    }

    const hideHoverTooltip = (): void => {
      hoverOverlay.value?.setPosition(undefined)
      hoverAuthor.value = ''
      hoverCreated.value = ''
      hoverNoteText.value = ''
    }

    const ensureHoverOverlay = (map: Map): void => {
      if (!hoverTooltipElement.value) {
        return
      }

      if (!hoverOverlay.value) {
        hoverOverlay.value = new Overlay({
          element: hoverTooltipElement.value,
          offset: [0, -18],
          positioning: 'bottom-center',
          stopEvent: false
        })
      }

      hoverOverlay.value.setMap(map)
    }

    const getMutationHeaders = async (): Promise<Record<string, string>> => {
      const csrfToken = await authStore.getCsrfToken()
      if (!csrfToken) {
        throw new Error('CSRF token not available')
      }

      return {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken
      }
    }

    const hydrateSelectedNote = (note: NoteItem | null): void => {
      selectedNote.value = note
      selectedNoteText.value = note?.note ?? ''
      selectedSharedGroupIds.value = note?.shared_groups?.map((group) => group.id) ?? []
    }

    const resetDraftState = (): void => {
      draftLocation.value = null
      draftLat.value = ''
      draftLon.value = ''
      draftNoteText.value = ''
      draftSharedGroupIds.value = []
    }

    watch([draftLat, draftLon], () => {
      const lat = parseFloat(draftLat.value)
      const lon = parseFloat(draftLon.value)
      if (!isNaN(lat) && !isNaN(lon)) {
        draftLocation.value = { lat, lon }
      } else {
        draftLocation.value = null
      }
    })

    const loadShareableGroups = async (): Promise<void> => {
      const siteIdValue = String(props.siteId).trim()
      if (!siteIdValue) {
        shareableGroups.value = []
        return
      }

      try {
        const response = await fetch('/api/sites/?format=json', {
          credentials: 'include'
        })
        if (!response.ok) {
          throw new Error(`Failed to load sites: ${response.status}`)
        }

        const payload = (await response.json()) as Array<{ id?: number; name?: string; groups?: Array<{ group?: number; group_name?: string }> }>
        const sites = Array.isArray(payload) ? payload : []
        const site = sites.find((s) => String(s.name).trim() === siteIdValue)

        if (site && typeof site.id === 'number') {
          resolvedSiteId.value = site.id
        }

        const groups = Array.isArray(site?.groups) ? site.groups : []
        shareableGroups.value = groups
          .filter((group) => typeof group.group === 'number')
          .map((group) => ({
            id: Number(group.group),
            name: String(group.group_name ?? group.group)
          }))
      } catch {
        // Keep this non-fatal so notes still work if group fetch fails.
        shareableGroups.value = []
      }
    }

    const loadNotes = async (): Promise<void> => {
      if (!isVisible.value) {
        return
      }

      const siteIdValue = String(props.siteId).trim()
      if (!siteIdValue) {
        return
      }

      isLoading.value = true
      errorMessage.value = ''

      try {
        const numericSiteId = resolvedSiteId.value
        if (!numericSiteId) {
          throw new Error('Site not yet resolved — try toggling notes off and on')
        }
        const response = await fetch(`/api/notes/?site=${numericSiteId}`, {
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch notes: ${response.status}`)
        }

        const payload = (await response.json()) as NoteItem[] | { results?: NoteItem[] }
        const unpacked = Array.isArray(payload)
          ? payload
          : Array.isArray(payload.results)
            ? payload.results
            : []
        notes.value = unpacked

        createLayerIfMissing()
        notesSource.value?.clear(true)
        notesSource.value?.addFeatures(notes.value.map(toFeature))
        loadedCount.value = notes.value.length

        if (selectedNote.value) {
          const refreshed = notes.value.find((note) => note.id === selectedNote.value?.id) ?? null
          hydrateSelectedNote(refreshed)
        }

        emit('notes-loaded', notes.value)
      } catch (error) {
        errorMessage.value = error instanceof Error ? error.message : 'Failed to load notes'
      } finally {
        isLoading.value = false
      }
    }

    const refreshLayer = async (): Promise<void> => {
      if (!resolvedSiteId.value) {
        await loadShareableGroups()
      }
      await loadNotes()
    }

    const toggleLayerVisibility = async (): Promise<void> => {
      isVisible.value = !isVisible.value
      emit('visibility-changed', isVisible.value)

      if (isVisible.value) {
        addLayerToMap()
        if (!resolvedSiteId.value) {
          await loadShareableGroups()
        }
        await loadNotes()
        return
      }

      if (notesLayer.value) {
        notesLayer.value.setVisible(false)
      }

      isAddMode.value = false
      hydrateSelectedNote(null)
    }

    const toggleAddMode = (): void => {
      isAddMode.value = !isAddMode.value
      if (!isAddMode.value) {
        resetDraftState()
      }
      if (isAddMode.value) {
        hydrateSelectedNote(null)
      }
    }

    const createNote = async (): Promise<void> => {
      if (!draftLocation.value || !draftNoteText.value.trim()) {
        return
      }

      isSubmitting.value = true
      errorMessage.value = ''

      try {
        const headers = await getMutationHeaders()
        const response = await fetch('/api/notes/', {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({
            site: resolvedSiteId.value,
            note: draftNoteText.value.trim(),
            lat: draftLocation.value.lat,
            lon: draftLocation.value.lon,
            shared_group_ids: draftSharedGroupIds.value
          })
        })

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}))
          throw new Error(payload?.detail || `Failed to create note: ${response.status}`)
        }

        const created = (await response.json()) as NoteItem
        await loadNotes()
        const reselected = notes.value.find((note) => note.id === created.id) ?? created
        hydrateSelectedNote(reselected)
        isAddMode.value = false
        resetDraftState()
      } catch (error) {
        errorMessage.value = error instanceof Error ? error.message : 'Failed to create note'
      } finally {
        isSubmitting.value = false
      }
    }

    const updateSelectedNote = async (): Promise<void> => {
      if (!selectedNote.value) {
        return
      }

      isSubmitting.value = true
      errorMessage.value = ''

      try {
        const updatePayload: Record<string, unknown> = {
          note: selectedNoteText.value.trim()
        }

        if (props.allowSharingEdit) {
          updatePayload.shared_group_ids = selectedSharedGroupIds.value
        }

        const headers = await getMutationHeaders()

        const response = await fetch(`/api/notes/${selectedNote.value.id}/`, {
          method: 'PATCH',
          credentials: 'include',
          headers,
          body: JSON.stringify(updatePayload)
        })

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}))
          throw new Error(payload?.detail || `Failed to update note: ${response.status}`)
        }

        await loadNotes()
      } catch (error) {
        errorMessage.value = error instanceof Error ? error.message : 'Failed to update note'
      } finally {
        isSubmitting.value = false
      }
    }

    const deleteSelectedNote = async (): Promise<void> => {
      if (!selectedNote.value) {
        return
      }

      const accepted = window.confirm('Delete this note?')
      if (!accepted) {
        return
      }

      isSubmitting.value = true
      errorMessage.value = ''

      try {
        const headers = await getMutationHeaders()
        const response = await fetch(`/api/notes/${selectedNote.value.id}/`, {
          method: 'DELETE',
          credentials: 'include',
          headers
        })

        if (!response.ok) {
          throw new Error(`Failed to delete note: ${response.status}`)
        }

        hydrateSelectedNote(null)
        await loadNotes()
      } catch (error) {
        errorMessage.value = error instanceof Error ? error.message : 'Failed to delete note'
      } finally {
        isSubmitting.value = false
      }
    }

    const handleMapClick = (event: MapBrowserEvent<UIEvent>): void => {
      if (!isVisible.value) {
        return
      }

      const map = getMapInstance()
      if (!map || !notesLayer.value) {
        return
      }

      if (isAddMode.value) {
        const lonLat = toLonLat(event.coordinate)
        draftLat.value = Number(lonLat[1]).toFixed(6)
        draftLon.value = Number(lonLat[0]).toFixed(6)
        draftLocation.value = {
          lat: Number(lonLat[1]),
          lon: Number(lonLat[0])
        }
        return
      }

      let selected: NoteFeatureProps | null = null
      map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
        if (layer !== notesLayer.value) {
          return undefined
        }

        selected = {
          noteId: Number(feature.get('noteId')),
          noteText: String(feature.get('noteText') ?? ''),
          authorUsername: String(feature.get('authorUsername') ?? ''),
          created: String(feature.get('created') ?? '')
        }

        return feature
      })

      if (selected) {
        const matching = notes.value.find((note) => note.id === selected?.noteId) ?? null
        hydrateSelectedNote(matching)
        emit('note-selected', selected)
      } else {
        hydrateSelectedNote(null)
      }
    }

    const handleMapPointerMove = (event: MapBrowserEvent<UIEvent>): void => {
      const map = getMapInstance()
      if (!map || !notesLayer.value || !isVisible.value || isAddMode.value) {
        if (map) {
          map.getTargetElement().style.cursor = ''
        }
        hideHoverTooltip()
        return
      }

      let hoveredFeature: Feature<Point> | null = null
      map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
        if (layer !== notesLayer.value) {
          return undefined
        }

        hoveredFeature = feature as Feature<Point>
        return feature
      })

      if (!hoveredFeature) {
        map.getTargetElement().style.cursor = ''
        hideHoverTooltip()
        return
      }

      const geometry = hoveredFeature.getGeometry()
      const coordinate = geometry instanceof Point ? geometry.getCoordinates() : undefined
      if (!coordinate) {
        map.getTargetElement().style.cursor = ''
        hideHoverTooltip()
        return
      }

      map.getTargetElement().style.cursor = 'pointer'
      ensureHoverOverlay(map)
      hoverAuthor.value = String(hoveredFeature.get('authorUsername') ?? '')
      hoverCreated.value = formatTimestamp(String(hoveredFeature.get('created') ?? ''))
      hoverNoteText.value = truncateNotePreview(String(hoveredFeature.get('noteText') ?? ''))
      hoverOverlay.value?.setPosition(coordinate)
    }

    watch(
      () => props.siteId,
      async () => {
        resetDraftState()
        hydrateSelectedNote(null)
        hideHoverTooltip()
        await loadShareableGroups()
        if (isVisible.value) {
          await loadNotes()
        }
      }
    )

    watch(
      () => mapStore.map,
      async (newMap) => {
        if (!newMap) {
          return
        }
        const map = getMapInstance()
        if (map) {
          ensureHoverOverlay(map)
          map.un('click', handleMapClick)
          map.un('pointermove', handleMapPointerMove)
          map.on('click', handleMapClick)
          map.on('pointermove', handleMapPointerMove)
        }
        if (!isVisible.value) {
          return
        }
        addLayerToMap()
        await loadNotes()
      }
    )

    onMounted(async () => {
      const map = getMapInstance()
      if (map) {
        ensureHoverOverlay(map)
        map.on('click', handleMapClick)
        map.on('pointermove', handleMapPointerMove)
      }

      await loadShareableGroups()

      if (isVisible.value) {
        addLayerToMap()
        await loadNotes()
      }
    })

    onBeforeUnmount(() => {
      const map = getMapInstance()
      if (map) {
        map.un('click', handleMapClick)
        map.un('pointermove', handleMapPointerMove)
        map.getTargetElement().style.cursor = ''
      }
      hoverOverlay.value?.setMap(null)
      removeLayerFromMap()
    })

    return {
      isVisible,
      isLoading,
      isSubmitting,
      isAddMode,
      loadedCount,
      errorMessage,
      selectedNote,
      selectedNoteText,
      selectedSharedGroupIds,
      shareableGroups,
      hoverTooltipElement,
      hoverAuthor,
      hoverCreated,
      hoverNoteText,
      draftLocation,
      draftLat,
      draftLon,
      draftNoteText,
      draftSharedGroupIds,
      allowSharingEdit: props.allowSharingEdit,
      canSaveDraft,
      formatTimestamp,
      toggleLayerVisibility,
      refreshLayer,
      toggleAddMode,
      createNote,
      updateSelectedNote,
      deleteSelectedNote
    }
  }
})
</script>

<style scoped>
.notes-layer-panel {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid var(--surface-border);
  border-radius: 0.6rem;
  background: var(--surface-0);
}

.notes-layer-panel__coords-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.notes-layer-panel__coord-label {
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.notes-layer-panel__coord-input {
  flex: 1;
  min-width: 6rem;
  font-size: 0.8rem;
}

.notes-layer-tooltip {
  display: block;
  max-width: 18rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 0.6rem;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
  color: #0f172a;
  font-size: 0.8rem;
  line-height: 1.35;
  pointer-events: none;
  backdrop-filter: blur(6px);
}

.notes-layer-tooltip__meta {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
  color: #475569;
  font-size: 0.72rem;
  font-weight: 600;
}

.notes-layer-tooltip__text {
  white-space: normal;
  overflow-wrap: anywhere;
}


.notes-layer-panel__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.notes-layer-panel__title {
  font-size: 0.85rem;
  font-weight: 600;
}

.notes-layer-panel__count {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.notes-layer-panel__actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.notes-layer-panel__error {
  margin: 0;
  color: #b91c1c;
  font-size: 0.78rem;
}

.notes-layer-panel__section {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding-top: 0.4rem;
  border-top: 1px dashed var(--surface-border);
}

.notes-layer-panel__section-title {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--text-color-secondary);
}

.notes-layer-panel__meta {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.notes-layer-panel__hint,
.notes-layer-panel__coords {
  margin: 0;
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.notes-layer-panel__textarea,
.notes-layer-panel__multiselect {
  width: 100%;
}

.notes-layer-panel__row-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
</style>
